import { useEffect, useRef, useState } from "react";
import { ScrollTrigger, usePrefersReducedMotion } from "@/hooks/useSmoothScroll";
import { DeviceFrameDesktop } from "@/components/DeviceFrames";
import { Reveal } from "@/components/Reveal";

// =====================================================================
// PinnedScreenSequence — reusable cinematic explainer (Phase 12 polish).
//
// Desktop (>=1024px, no reduced motion):
//   Right stage stays pinned (CSS sticky, Lenis-safe — same pattern as
//   the DealAlert scrollytelling). Left explainer steps scroll and drive
//   the active step via ScrollTrigger. The active screenshot crossfades,
//   the stage zooms onto the relevant area (transform-only) and an
//   optional spotlight highlight dims everything around the key region.
//
// Mobile + prefers-reduced-motion:
//   Stacked fallback (image + text per step), no pinning, no zoom.
//
// step shape:
// {
//   media: { src, alt, w, h },
//   kicker, title, text,                  // copy (from translations)
//   focus: { x, y, scale },               // zoom target in % of image
//   highlight: { x, y, w, h } | null,     // spotlight box in % of image
// }
// =====================================================================

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const zoomTransform = (focus) => {
  if (!focus || !focus.scale || focus.scale <= 1) return "scale(1) translate(0%, 0%)";
  const dx = clamp(50 - focus.x, -50, 50);
  const dy = clamp(50 - focus.y, -50, 50);
  return `scale(${focus.scale}) translate(${dx}%, ${dy}%)`;
};

const StepCopy = ({ step, index, total, active, accent, registerRef }) => (
  <div
    ref={(el) => registerRef(index, el)}
    className={`min-h-[82vh] flex flex-col justify-center transition-opacity duration-500 ${
      active ? "opacity-100" : "opacity-30"
    }`}
    data-testid={`seq-step-${index + 1}`}
  >
    <p className="flex items-baseline gap-3">
      <span className="font-display text-2xl font-semibold tabular-nums" style={{ color: accent }}>
        {String(index + 1).padStart(2, "0")}
      </span>
      <span className="text-[10px] font-mono-cs uppercase tracking-[0.22em] text-muted-foreground/70">
        / {String(total).padStart(2, "0")} — {step.kicker}
      </span>
    </p>
    <h3 className="mt-3 font-display text-xl sm:text-2xl font-semibold tracking-[-0.015em] leading-snug text-foreground max-w-md">
      {step.title}
    </h3>
    <p className="mt-3 text-[15px] text-muted-foreground leading-relaxed max-w-md">{step.text}</p>
  </div>
);

const Stage = ({ steps, active, accent, url, frame, aspect, testId }) => {
  const activeFocus = steps[active]?.focus;
  const inner = (
    <div className="relative overflow-hidden bg-[#0a0f18]" style={{ aspectRatio: aspect }}>
      <div className="cs-seq-zoom absolute inset-0" style={{ transform: zoomTransform(activeFocus) }}>
        {steps.map((s, i) => (
          <img
            key={s.media.src + i}
            src={s.media.src}
            alt={s.media.alt}
            width={s.media.w}
            height={s.media.h}
            loading={i === 0 ? "eager" : "lazy"}
            className={`cs-seq-img absolute inset-0 w-full h-full object-cover ${active === i ? "is-active" : ""}`}
          />
        ))}
        {steps.map(
          (s, i) =>
            s.highlight && (
              <span
                key={`hl-${i}`}
                aria-hidden="true"
                className={`cs-seq-highlight absolute rounded-md ${active === i ? "is-active" : ""}`}
                style={{
                  left: `${s.highlight.x}%`,
                  top: `${s.highlight.y}%`,
                  width: `${s.highlight.w}%`,
                  height: `${s.highlight.h}%`,
                  border: `2px solid ${accent}`,
                  boxShadow: `0 0 0 9999px rgba(7,10,18,0.45), 0 0 22px ${accent}55`,
                }}
              />
            )
        )}
      </div>
    </div>
  );

  return (
    <div className="sticky top-0 h-screen flex flex-col items-center justify-center">
      {frame === "document" ? (
        <div
          data-testid={testId ? `${testId}-stage` : undefined}
          className="rounded-lg border border-white/15 shadow-[0_18px_60px_rgba(0,0,0,0.5)] overflow-hidden"
          style={{ height: "min(80vh, 860px)", aspectRatio: aspect }}
        >
          {inner}
        </div>
      ) : (
        <div className="w-full" data-testid={testId ? `${testId}-stage` : undefined}>
          <DeviceFrameDesktop url={url}>{inner}</DeviceFrameDesktop>
        </div>
      )}
      {/* Progress dots */}
      <div className="mt-6 flex items-center gap-2.5" aria-hidden="true">
        {steps.map((s, i) => (
          <span
            key={i}
            className="h-1.5 rounded-full transition-all duration-500"
            style={{
              width: active === i ? "30px" : "6px",
              backgroundColor: active === i ? accent : "rgba(255,255,255,0.15)",
            }}
          />
        ))}
      </div>
    </div>
  );
};

const PinnedVariant = ({ steps, accent, url, frame, aspect, testId }) => {
  const [active, setActive] = useState(0);
  const stepRefs = useRef([]);
  const registerRef = (i, el) => {
    stepRefs.current[i] = el;
  };

  useEffect(() => {
    const triggers = stepRefs.current.filter(Boolean).map((el, i) =>
      ScrollTrigger.create({
        trigger: el,
        start: "top 55%",
        end: "bottom 55%",
        onEnter: () => setActive(i),
        onEnterBack: () => setActive(i),
      })
    );
    return () => triggers.forEach((tr) => tr.kill());
  }, []);

  return (
    <div className="grid grid-cols-12 gap-10" data-testid={testId}>
      <div className="col-span-5">
        {steps.map((s, i) => (
          <StepCopy key={i} step={s} index={i} total={steps.length} active={active === i} accent={accent} registerRef={registerRef} />
        ))}
      </div>
      <div className="col-span-7">
        <Stage steps={steps} active={active} accent={accent} url={url} frame={frame} aspect={aspect} testId={testId} />
      </div>
    </div>
  );
};

const StackedVariant = ({ steps, accent, url, frame, aspect, testId }) => (
  <div className="space-y-14" data-testid={testId}>
    {steps.map((s, i) => (
      <Reveal key={i}>
        {frame === "document" ? (
          <div className="rounded-lg border border-white/15 overflow-hidden max-w-[420px] mx-auto shadow-[0_10px_36px_rgba(0,0,0,0.4)]">
            <img src={s.media.src} alt={s.media.alt} width={s.media.w} height={s.media.h} loading="lazy" className="block w-full h-auto" />
          </div>
        ) : (
          <DeviceFrameDesktop url={url}>
            <div className="overflow-hidden" style={{ aspectRatio: aspect }}>
              <img src={s.media.src} alt={s.media.alt} width={s.media.w} height={s.media.h} loading="lazy" className="block w-full h-full object-cover" />
            </div>
          </DeviceFrameDesktop>
        )}
        <p className="mt-5 flex items-baseline gap-3">
          <span className="font-display text-xl font-semibold tabular-nums" style={{ color: accent }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="text-[10px] font-mono-cs uppercase tracking-[0.22em] text-muted-foreground/70">{s.kicker}</span>
        </p>
        <h3 className="mt-2 font-display text-lg sm:text-xl font-semibold tracking-[-0.015em] text-foreground">{s.title}</h3>
        <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed max-w-xl">{s.text}</p>
      </Reveal>
    ))}
  </div>
);

export const PinnedScreenSequence = ({
  steps,
  accent = "#00d4ff",
  url = "",
  frame = "browser",
  aspect = "1920/1098",
  stackedFallback = true,
  testId = "pinned-sequence",
}) => {
  const reduced = usePrefersReducedMotion();
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);

  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  if (!steps || steps.length === 0) return null;

  if (isDesktop && !reduced) {
    return <PinnedVariant steps={steps} accent={accent} url={url} frame={frame} aspect={aspect} testId={testId} />;
  }
  if (!stackedFallback) return null;
  return <StackedVariant steps={steps} accent={accent} url={url} frame={frame} aspect={aspect} testId={testId} />;
};
